import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../services/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔍 Full URL:', window.location.href);
        console.log('🔍 Hash fragment:', window.location.hash);
        console.log('🔍 Search params:', window.location.search);

        // Check for errors in URL first
        const searchParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));

        const errorFromQuery = searchParams.get('error');
        const errorFromHash = hashParams.get('error');
        const errorDescFromQuery = searchParams.get('error_description');
        const errorDescFromHash = hashParams.get('error_description');

        const error = errorFromQuery || errorFromHash;
        const errorDescription = errorDescFromQuery || errorDescFromHash;

        if (error) {
          console.error('❌ Auth error in URL:', error, errorDescription);
          if (error === 'access_denied' && errorDescription?.includes('expired')) {
            setError('This invitation link has expired. Please contact your organization owner to resend the invitation.');
          } else {
            setError(errorDescription || 'Authentication failed. Please try again.');
          }
          return;
        }

        // Check for query parameters (PKCE flow - newer invite flow)
        const tokenHash = searchParams.get('token_hash');
        const typeFromQuery = searchParams.get('type');

        // Check for hash parameters (implicit flow - older flow)
        const accessToken = hashParams.get('access_token');
        const typeFromHash = hashParams.get('type');

        const type = typeFromQuery || typeFromHash;

        console.log('🔍 Parsed parameters:', {
          tokenHash: tokenHash ? 'present' : 'missing',
          accessToken: accessToken ? 'present' : 'missing',
          type: type,
          flow: tokenHash ? 'PKCE (query params)' : 'Implicit (hash)'
        });

        // For PKCE flow (token_hash in query params), we need to exchange it
        if (tokenHash && !accessToken) {
          console.log('🔄 PKCE flow detected, exchanging token_hash for session');
          // For invite flow with token_hash, redirect to set password directly
          // The token will be verified on the set-password page
          if (type === 'invite') {
            console.log('✅ Invite with PKCE - redirecting to set-password with token');
            // Store the token_hash temporarily
            sessionStorage.setItem('invite_token_hash', tokenHash);
            window.location.href = '/set-password';
            return;
          }
          if (type === 'recovery') {
            console.log('🔄 Password recovery with PKCE - verifying token_hash');
            // Verify here (this page has no RequireAuth guard) so a valid
            // session token is in place before /set-password mounts.
            const recoveryResponse = await api.post('/auth/verify-recovery', { token_hash: tokenHash });
            localStorage.setItem('token', recoveryResponse.data.token);
            sessionStorage.setItem('password_reset', '1');
            console.log('✅ Recovery verified - redirecting to set-password');
            window.location.href = '/set-password';
            return;
          }
        }

        if (!accessToken && !tokenHash) {
          setError('No access token found in URL');
          return;
        }

        // Exchange the Supabase token for user profile
        const response = await api.post('/auth/callback', {
          access_token: accessToken
        });

        // Store the token
        localStorage.setItem('token', response.data.token);

        // For invites, redirect to password setup first
        console.log('🔍 Type parameter value:', type);
        console.log('🔍 Is invite?', type === 'invite');

        if (type === 'invite' || type === 'recovery') {
          if (type === 'recovery') {
            sessionStorage.setItem('password_reset', '1');
          }
          console.log('✅ Redirecting to set-password');
          // Force page reload to refresh auth context with new token
          window.location.href = '/set-password';
        } else {
          console.log('✅ Redirecting to dashboard (type was:', type, ')');
          // For other auth types, go to dashboard
          window.location.href = '/dashboard';
        }

      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.response?.data?.message || 'Failed to complete authentication');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
        <p className="text-lg text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
